import * as THREE from 'three';
import store from '../redux/store';
import { addCamera, addLight, selected_object } from '../redux/Slice/gridSlice';
import { scheduleSelectLightInScene } from '../utils/ThreeJsUtils/lightHelperUtils';
import { setSelectedLightId } from '../redux/Slice/globalStates';
import { setSelectedParticle, addParticle } from '../redux/Slice/particleSlice';
import { duplicateParticleAsset } from '../utils/particles/HandleDuplicatePartical';
// import { addCamera } from '../redux/Slice/cameraSlice';

export const findItem = (
  selected,
  { light_data = [], assets_data = {}, particles = [] }
) => {
  if (!selected) return null;

  // Lights
  if (
    selected.isLight ||
    selected.isDirectionalLight ||
    selected.isAmbientLight ||
    selected.isPointLight ||
    selected.isSpotLight ||
    selected.isRectAreaLight
  ) {
    const light = light_data.find((l) => l.id === selected.name);
    if (light) {
      return { type: 'light', data: { ...light } };
    }
  }

  // Cameras
  if (selected.isCamera) {
    return {
      type: 'camera',
      data: {
        type: selected.type,
        fov: selected.fov,
        aspect: selected.aspect,
        near: selected.near,
        far: selected.far,
        left: selected.left,
        right: selected.right,
        top: selected.top,
        bottom: selected.bottom,
        zoom: selected.zoom,
        position: selected.position.toArray(),
        rotation: selected.rotation.toArray(),
        scale: selected.scale.toArray(),
      },
    };
  }

  // Assets
  const arrKeys = ['image', 'mesh', 'video', 'audio', 'text','hotspot'];
  for (const key of arrKeys) {
    const asset = assets_data[key]?.find(
      (a) =>
        a._id === selected.name ||
        a.url === selected.url ||
        a.name === selected.name
    );
    if (asset) return { type: key, data: { ...asset } };
  }

  // Particles - check multiple fields for matching
  if (selected.deleteType === 'particle' || selected.objType) {
    const particle = particles.find(
      (p) =>
        p.id === selected.name ||
        p.id === selected.url ||
        p._id === selected.name ||
        p._id === selected.url ||
        p.name === selected.name ||
        (selected.userData && p.id === selected.userData.id)
    );
    if (particle) return { type: 'particle', data: { ...particle } };
    
    // If particle not found in Redux but object has particle properties, create data from object
    if (selected.objType || selected.deleteType === 'particle') {
      const particleData = {
        id: selected.name || selected.url || `particle_${Date.now()}`,
        name: selected.name || selected.file_name || selected.displayName || 'particle',
        type: selected.objType || 'fire',
        position: selected.position
          ? { x: selected.position.x, y: selected.position.y, z: selected.position.z }
          : { x: 0, y: 0, z: 0 },
        rotation: selected.rotation
          ? {
              x: THREE.MathUtils.radToDeg(selected.rotation.x),
              y: THREE.MathUtils.radToDeg(selected.rotation.y),
              z: THREE.MathUtils.radToDeg(selected.rotation.z),
            }
          : { x: 0, y: 0, z: 0 },
        scale: selected.scale
          ? { x: selected.scale.x, y: selected.scale.y, z: selected.scale.z }
          : { x: 1, y: 1, z: 1 },
        ...(selected.userData || {}),
      };
      return { type: 'particle', data: particleData };
    }
  }

  return null;
};

export const pasteItem =
  ({
    dispatch,
    scene,
    selectObjectInScene,
    setSelectedObjectState,
    setAdditionalCameras,
    objectt, // Add objectt parameter for particle duplication
  }) =>
  (item) => {
    switch (item.type) {
      case 'light': {
        // Clear any existing light selection first - this is the fix
        dispatch(setSelectedLightId(null));

        const newId = `light_${Date.now()}`;
        const currentPosition = item.data.position || { x: 0, y: 0, z: 0 };

        const newLightData = {
          ...item.data,
          id: newId,
          _id: undefined, // Ensure new ID is generated
          name: `${item.data.name} copy`,
          position: {
            x: currentPosition.x + 2,
            y: currentPosition.y + 2,
            z: currentPosition.z,
          },
          temp_file_name: `${item.data.name} copy-${Date.now()}`,
        };

        dispatch(addLight(newLightData));
        dispatch(setSelectedLightId(newId));
        scheduleSelectLightInScene(scene, newId, selectObjectInScene);

        break;
      }
      case 'camera': {
        let newCamera;

        if (item.data.type === 'PerspectiveCamera') {
          newCamera = new THREE.PerspectiveCamera(
            item.data.fov,
            item.data.aspect,
            item.data.near,
            item.data.far
          );
        } else if (item.data.type === 'OrthographicCamera') {
          newCamera = new THREE.OrthographicCamera(
            item.data.left,
            item.data.right,
            item.data.top,
            item.data.bottom,
            item.data.near,
            item.data.far
          );
          newCamera.zoom = item.data.zoom;
        }

        if (newCamera) {
          newCamera.position.fromArray(
            item.data.position.map((p, idx) => (idx < 2 ? p + 1 : p))
          );
          newCamera.rotation.fromArray(item.data.rotation);
          newCamera.scale.fromArray(item.data.scale);

          // Use the source name (if any) to build a nicer duplicate label
          const sourceLabel =
            item.data.name ||
            item.data.file_name ||
            item.data.temp_file_name ||
            `Camera`;
          const newCameraName = `${sourceLabel}_${Date.now()}`;
          newCamera.name = newCameraName;
          scene.add(newCamera);

          // Push into Redux
          dispatch(
            addCamera({
              _id: newCameraName,
              id: newCameraName,
              name: `${sourceLabel}`,
              temp_file_name: `${sourceLabel}`,
              file_name: `${sourceLabel}`,
              type:
                item.data.type === 'PerspectiveCamera'
                  ? 'Perspective'
                  : 'Orthographic',
              fov: item.data.fov,
              aspect: item.data.aspect,
              near: item.data.near,
              far: item.data.far,
              left: item.data.left,
              right: item.data.right,
              top: item.data.top,
              bottom: item.data.bottom,
              zoom: item.data.zoom,
              position: newCamera.position.toArray(),
              rotation: newCamera.rotation.toArray(),
              scale: newCamera.scale.toArray(),
              helperVisible: false,
            })
          );

          setAdditionalCameras((prev) => [...prev, newCamera]);
          store.dispatch(selected_object(newCamera));
          selectObjectInScene(newCamera.name);
          setSelectedObjectState(newCamera);
        }
        break;
      }

      case 'particle': {
        // Use the proper duplication function that handles all particle types correctly
        if (!objectt) {
          console.error('objectt is required for particle duplication');
          break;
        }
        console.log('item.datajdjd', item.data);

        // Use duplicateParticleAsset which handles all particle types properly
        duplicateParticleAsset(item.data, objectt);
        
        break;
      }

      // default: {
      //   console.warn('⚠️ Unhandled paste type:', item.type);
      //   break;
      // }
    }
  };
